export type AdminView = {
  id: string;
  name: string;
  email: string;
  phone: string;
  roles: string[];
  createdAt: string;
  updatedAt: string;
};

export type AppConfigType = "boolean" | "integer" | "string" | "url";

export type AppConfigScope = "runtime" | "boot";

export type AppConfigOrigin = "registry" | "custom";

export type AdminGovernorateView = {
  id: string;
  nameAr: string;
  nameEn: string | null;
  isActive: boolean;
};

export type AdminAreaView = {
  id: string;
  governorateId: string;
  nameAr: string;
  nameEn: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

export type AreaSuggestionStatus = "pending" | "approved" | "rejected";

export type AdminAreaSuggestionView = {
  id: string;
  governorateId: string;
  rawName: string;
  normalizedName: string;
  status: AreaSuggestionStatus;
  resolvedAreaId: string | null;
  occurrenceCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  governorateNameAr: string;
  governorateNameEn: string | null;
};

export type ApproveAreaSuggestionResult = {
  suggestion: Omit<
    AdminAreaSuggestionView,
    "governorateNameAr" | "governorateNameEn"
  >;
  area: {
    id: string;
    governorateId: string;
    nameAr: string;
    nameEn: string | null;
    isActive: boolean;
  };
};

export type AppConfigView = {
  // Stable technical id — used for the API route and shown as a small
  // monospace subtitle in the UI. The user-facing label is `nameEn`/
  // `nameAr` selected by the active locale.
  key: string;
  value: string | null;
  // Effective default shown when no override row exists (registry default,
  // or the stored value for custom keys).
  defaultValue: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  type: AppConfigType;
  scope: AppConfigScope;
  origin: AppConfigOrigin;
  min?: number;
  max?: number;
  source: "db" | "unset";
  updatedAt: string | null;
};

export type AppConfigAuditView = {
  id: string;
  key: string;
  oldValue: string | null;
  // newValue is null when the row was reset (deleted) to the env default.
  newValue: string | null;
  actorId: string | null;
  actorEmail: string | null;
  changedAt: string;
};

// Feature flags — in-house, per-store targeted. Independent of AppConfig.
export type FeatureFlagStoreView = {
  id: string;
  storeName: string;
  storeSlug: string;
};

export type FeatureFlagView = {
  id: string;
  key: string;
  nameEn: string;
  nameAr: string | null;
  descriptionEn: string | null;
  descriptionAr: string | null;
  // Master kill-switch: when false the flag is off for every store.
  isActive: boolean;
  // When true the allow-list is bypassed and the flag targets all stores.
  enableForAllStores: boolean;
  createdAt: string;
  updatedAt: string;
  // The explicit allow-list (empty when enableForAllStores).
  targetStores: FeatureFlagStoreView[];
};

export type FeatureFlagAuditView = {
  id: string;
  flagKey: string;
  action: string;
  detail: string | null;
  actorId: string | null;
  actorEmail: string | null;
  changedAt: string;
};

export type ThemeStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";
// A backend-owned identifier — the catalog now carries the seeded `eo-*` family
// themes (avyro, elite, …) alongside `starter`, and it can grow again. Admin
// only DISPLAYS it, so it is typed as a string rather than a closed union that
// would silently go stale (and, worse, crash the themes list when the API
// returns a family the admin's map doesn't know — see FAMILY_LABEL_KEY usages,
// which now fall back to the raw family name). The create/edit forms still
// offer only `THEME_FAMILIES` (["starter"]) — admins don't hand-author families.
export type ThemeFamily = string;
export type ThemeVersion = {
  id: string;
  themeId: string;
  version: number;
  schemaVersion: number;
  json: Record<string, unknown>;
  thumbnailUrl: string | null;
  publishedAt: string | null;
  changelogAr: string | null;
  changelogEn: string | null;
  createdAt: string;
};
export type Theme = {
  id: string;
  family: ThemeFamily;
  slug: string;
  nameAr: string;
  nameEn: string;
  descriptionAr: string | null;
  descriptionEn: string | null;
  niches: string[];
  status: ThemeStatus;
  sortWeight: number;
  createdAt: string;
  updatedAt: string;
};
export type ThemeWithVersions = Theme & {
  versions: ThemeVersion[];
};
export type ThemeListResponse = ThemeWithVersions[];
export const THEME_FAMILIES: ThemeFamily[] = ["starter"];
export const THEME_NICHES = [
  "watches",
  "clothing",
  "electronics",
  "games",
  "digital",
  "health",
  "pets",
  "beauty",
] as const;
export type ThemeNiche = (typeof THEME_NICHES)[number];
// Publish validation errors surfaced under ApiJsonClientError.payload.errors.
export type ThemeValidationErrorItem = {
  path: string;
  code: string;
  message: string;
};

export type SellerView = {
  id: string;
  name: string;
  email: string;
  phone: string;
  createdAt: string;
  updatedAt: string;
};
export type SellerOptionView = {
  id: string;
  name: string;
};
export type StoreView = {
  id: string;
  sellerId: string;
  storeName: string;
  storeSlug: string;
  storeShort: string;
  description: string | null;
  logoUrl: string | null;
  maxProductImageCount: number;
  depositPercent: number | null;
  defaultShippingFee: number | null;
  defaultDepositPercent: number | null;
  defaultIncludeShippingInRequiredAmount: boolean;
  currency: string;
  storeHighlights?: StoreHighlightView[];
  storePolicies?: StorePolicyView[];
  createdAt: string;
  updatedAt: string;
};
export type StorePolicyType = "SHIPPING" | "REFUND" | "EXCHANGE";
export type StorePolicyView = {
  id: string;
  storeId: string;
  policyType: StorePolicyType;
  isEnabled: boolean;
  titleAr: string;
  titleEn: string;
  contentAr: string;
  contentEn: string;
  createdAt: string;
  updatedAt: string;
};

export type SellerListItem = SellerView & {
  stores: StoreView[];
};
export type SellerListResponse = {
  items: SellerListItem[];
  total: number;
  limit: number;
  offset: number;
};
export type SellerOptionsResponse = {
  items: SellerOptionView[];
};
export type StoreListItem = StoreView & {
  seller: SellerOptionView;
};
export type StoreOptionView = {
  id: string;
  storeName: string;
  sellerName: string;
};
export type StoreOptionsResponse = {
  items: StoreOptionView[];
};
export type StoreListResponse = {
  items: StoreListItem[];
  total: number;
  limit: number;
  offset: number;
};
export type StoreAdminDetailResponse = {
  store: StoreView;
  seller: {
    id: string;
  };
};
export type StoreAdminStatsResponse = {
  orders: {
    total: number;
    pending: number;
    delivered: number;
  };
  catalog: {
    products: number;
    categories: number;
  };
};
export type StoreImpersonationSessionResponse = {
  storeSlug: string;
};
export const SUBSCRIPTION_PLAN_TYPE = {
  FREE_TRIAL: "FREE_TRIAL",
  MONTHLY_SUBSCRIPTION: "MONTHLY_SUBSCRIPTION",
} as const;

export type SubscriptionPlanType =
  (typeof SUBSCRIPTION_PLAN_TYPE)[keyof typeof SUBSCRIPTION_PLAN_TYPE];

export type SubscriptionPlanView = {
  id: string;
  nameAr: string;
  nameEn: string | null;
  type: SubscriptionPlanType;
  amount: number;
  currency: string;
  periodDays: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};
export type SubscriptionPlanListResponse = {
  items: SubscriptionPlanView[];
};
export type StoreSubscriptionAssignmentView = {
  id: string;
  storeId: string;
  planId: string | null;
  planNameAr: string;
  planNameEn: string | null;
  type: SubscriptionPlanType;
  amount: number;
  currency: string;
  creditBalance: number;
  periodDays: number;
  startDate: string;
  reason: string;
  createdByAdmin: { id: string; name: string };
  createdAt: string;
};
export type StoreSubscriptionPaymentView = {
  id: string;
  storeId: string;
  assignmentId: string;
  scheduleId: string | null;
  amount: number;
  currency: string;
  paidAt: string;
  scheduleDueDate?: string | null;
  settledByAdmin: { id: string; name: string };
  proofObjectKey: string | null;
  proofContentType: string | null;
  proofSizeBytes: number | null;
  proofUrl?: string | null;
  deletedAt: string | null;
  deletedByAdmin: { id: string; name: string } | null;
  deleteReason: string | null;
  createdAt: string;
  allocations?: StoreSubscriptionPaymentAllocationView[];
};
export type StoreSubscriptionPaymentProofUrlResponse = {
  url: string | null;
};
export type StoreSubscriptionStatusItem = {
  storeId: string;
  storeName: string;
  storeSlug: string;
  currency: string;
  planNameAr: string;
  planNameEn: string | null;
  type: SubscriptionPlanType;
  amount: number;
  creditBalance: number;
  periodDays: number;
  startDate: string;
  lastPaymentAt: string | null;
  nextPaymentDate: string | null;
  isOverdue: boolean;
  isPartiallyPaid: boolean;
  isFreeTrial: boolean;
};
export type StoreSubscriptionListResponse = {
  items: StoreSubscriptionStatusItem[];
  total: number;
  limit: number;
  offset: number;
};
export type StoreSubscriptionCurrentScheduleView = {
  id: string;
  dueDate: string;
  amountDue: number;
  paidAmount: number;
  currency: string;
  status: StoreSubscriptionScheduleStatus;
  remainingAmount: number;
  creditBalance: number;
};
export type StoreSubscriptionPaymentAllocationView = {
  id: string;
  storeId: string;
  assignmentId: string;
  paymentId: string;
  paymentPaidAt: string | null;
  scheduleId: string | null;
  kind: StoreSubscriptionAllocationKind;
  amount: number;
  remainingAmount: number;
  deletedAt: string | null;
  createdAt: string;
};
export type StoreSubscriptionScheduleLedgerItemView = {
  schedule: StoreSubscriptionPaymentScheduleView;
  allocations: StoreSubscriptionPaymentAllocationView[];
};
export type StoreSubscriptionLedgerResponse = {
  schedules: StoreSubscriptionScheduleLedgerItemView[];
  creditAllocations: StoreSubscriptionPaymentAllocationView[];
  creditBalance: number;
  currency: string;
};
export type StoreSubscriptionPaymentScheduleView = {
  id: string;
  storeId: string;
  assignmentId: string;
  dueDate: string;
  amountDue: number;
  paidAmount: number;
  currency: string;
  status: StoreSubscriptionScheduleStatus;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
};
export const STORE_SUBSCRIPTION_ALLOCATION_KIND = {
  SCHEDULE: "SCHEDULE",
  CREDIT: "CREDIT",
} as const;

export type StoreSubscriptionAllocationKind =
  (typeof STORE_SUBSCRIPTION_ALLOCATION_KIND)[keyof typeof STORE_SUBSCRIPTION_ALLOCATION_KIND];

export const STORE_SUBSCRIPTION_SCHEDULE_STATUS = {
  PENDING: "PENDING",
  PARTIAL: "PARTIAL",
  PAID: "PAID",
} as const;

export type StoreSubscriptionScheduleStatus =
  (typeof STORE_SUBSCRIPTION_SCHEDULE_STATUS)[keyof typeof STORE_SUBSCRIPTION_SCHEDULE_STATUS];

export type SellerWithStoresResponse = {
  seller: SellerView;
  stores: StoreView[];
};
export type SellerInviteResponse = {
  inviteToken: string;
  expiresAt: string;
};
export type PaymentMethod = "COD" | "EXTERNAL_ONLINE";
export type OrderStatus =
  | "draft"
  | "paid_less_than_required"
  | "pending_buyer_verification"
  | "pending_buyer_payment"
  | "pending_payment_verification"
  | "confirmed"
  | "preparing"
  | "shipped"
  | "delivered"
  | "cancelled"
  | "declined";

export type DeclineReasonCode = "amount_not_received" | "amount_not_match";
export type PaymentReviewRejectReasonCode =
  | "amount_mismatch"
  | "proof_unclear"
  | "wrong_proof"
  | "suspected_fake"
  | "other";
export type ApproveOrderPaymentRequest = {
  approvedAmount: number;
  note?: string;
};
export type RejectOrderPaymentRequest = {
  reasonCode: PaymentReviewRejectReasonCode;
  note?: string;
};
export type OnlinePaymentProvider =
  | "paymob"
  | "paytabs"
  | "kashier"
  | "fawry"
  | "stripe";
export type OnlinePaymentStatus =
  | "initiated"
  | "pending"
  | "paid"
  | "failed"
  | "refunded"
  | "cancelled"
  | "expired";
/** How an online order was paid — mirrors the backend SellerOrderPaymentView. */
export type AdminOrderPaymentView = {
  provider: OnlinePaymentProvider;
  status: OnlinePaymentStatus;
  amount: number;
  currency: string;
  providerTxnId: string | null;
  lastEventAt: string | null;
  createdAt: string;
};
export type AdminOrderPaymentResponse = {
  transaction: AdminOrderPaymentView | null;
};
export type RefundOrderRequest = {
  amount?: number;
};

export type StoreHighlightView = {
  id: string;
  storeId: string;
  textAr: string;
  textEn: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
};
export type OrderItemSelectedOption = {
  optionGroupId: string;
  optionValueId: string;
  group: {
    ar: string;
    en: string | null;
  };
  value: {
    ar: string;
    en: string | null;
  };
};
export type OrderItemView = {
  productId: string;
  productVariantId: string;
  productNameAr: string;
  productNameEn: string | null;
  productSlug: string;
  resolvedTitleAr: string;
  resolvedTitleEn: string | null;
  selectedOptions: OrderItemSelectedOption[];
  quantity: number;
  unitPrice: number;
  unitOriginalPrice: number | null;
};
export type SellerOrderCustomer = {
  id: string;
  name: string;
  phoneE164: string;
};

// Structured delivery-address snapshot. `null` for legacy orders created
// before structured addresses — those fall back to the composed
// `buyerAddress` string. Mirrors the backend `OrderAddressView`.
export type OrderAddressView = {
  area: string;
  street: string;
  building: string;
  floor: string | null;
  apartment: string | null;
  landmark: string | null;
};

export type SellerOrderListItem = {
  id: string;
  orderCode: string;
  customer: SellerOrderCustomer;
  buyerAddress: string;
  address: OrderAddressView | null;
  paymentMethod: PaymentMethod;
  status: OrderStatus;
  statusEtaDays: number | null;
  declineReasonCode: DeclineReasonCode | null;
  declineReasonNote: string | null;
  paidAmount: number;
  requiredAmount: number;
  totalAmount: number;
  shippingFee: number;
  couponCode: string | null;
  couponDiscountAmount: number;
  createdAt: string;
  items: OrderItemView[];
};
export type OrderPaymentProofView = {
  id: string;
  orderId: string;
  objectKey: string;
  paidAmount: number;
  uploadedAt: string;
};
export type SellerOrderDetail = SellerOrderListItem & {
  buyerNote: string | null;
  paymentDetailId: string | null;
  depositPercent: number | null;
  updatedAt: string;
  items: OrderItemView[];
  paymentProofs: OrderPaymentProofView[];
  statusHistory: OrderStatusHistoryView[];
};
export type OrderStatusHistoryView = {
  id: string;
  orderId: string;
  oldStatus: OrderStatus;
  newStatus: OrderStatus;
  statusEtaDays: number | null;
  changedByType: "buyer" | "seller" | "admin";
  changedById: string | null;
  declineReasonCode: DeclineReasonCode | null;
  declineReasonNote: string | null;
  note: string | null;
  changedAt: string;
};
export type OrderPaymentProofUrlItem = {
  proofId: string;
  objectKey: string;
  paidAmount: number;
  uploadedAt: string;
  url: string;
};
export type OrderPaymentProofUrlResponse = {
  items: OrderPaymentProofUrlItem[];
};

export const INTEGRATION_LOG_DIRECTION = {
  OUTBOUND: "outbound",
  INBOUND: "inbound",
} as const;

export type IntegrationLogDirection =
  (typeof INTEGRATION_LOG_DIRECTION)[keyof typeof INTEGRATION_LOG_DIRECTION];

export const INTEGRATION_LOG_STATUS = {
  SUCCESS: "success",
  FAILED: "failed",
  PENDING: "pending",
} as const;

export type IntegrationLogStatus =
  (typeof INTEGRATION_LOG_STATUS)[keyof typeof INTEGRATION_LOG_STATUS];

/**
 * One row of the external-request audit log (`GET /admin/integration-logs`).
 * Every optional-looking field is nullable rather than absent because the
 * backend emits the full shape for both directions (an inbound webhook has no
 * `durationMs`, an unmatched request has no `storeId`, etc.).
 */
export type IntegrationLogListItem = {
  id: string;
  occurredAt: string;
  provider: string;
  direction: IntegrationLogDirection;
  status: IntegrationLogStatus;
  operation: string | null;
  method: string | null;
  url: string | null;
  httpStatus: number | null;
  durationMs: number | null;
  storeId: string | null;
  storeName: string | null;
  entityType: string | null;
  entityId: string | null;
  reference: string | null;
  correlationId: string | null;
  error: string | null;
};

export type IntegrationLogListResponse = {
  items: IntegrationLogListItem[];
  total: number;
  limit: number;
  offset: number;
};

/** JSON payload as stored by the backend — already redacted server-side. */
export type IntegrationLogPayload =
  | string
  | number
  | boolean
  | null
  | IntegrationLogPayload[]
  | { [key: string]: IntegrationLogPayload };

export type IntegrationLogDetail = IntegrationLogListItem & {
  requestPayload: IntegrationLogPayload;
  responsePayload: IntegrationLogPayload;
};
